import { FC } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, type LucideIcon } from "lucide-react";
import usePermissions from "src/hooks/usePermissions/usePermissions";
import type { Permission } from "src/constants/permissions";

export interface HomeSectionSubLink {
	label: string;
	path: string;
	icon: LucideIcon;
	/**
	 * If set, the sub-link is hidden from users who lack this permission.
	 * Permissions are only consulted after the {@link usePermissions} query
	 * has resolved — during loading the card returns null (matching the
	 * {@link UserAdminFilter} convention), so users never see a link flash
	 * then disappear once their permissions arrive.
	 */
	permission?: Permission | string;
}

export interface HomeSectionOpenLink {
	label: string;
	path: string;
}

interface BaseProps {
	title: string;
	description: string;
	icon: LucideIcon;
}

/**
 * Discriminated union — exactly one of {@link openLink} or {@link subLinks}
 * must be supplied. Passing both (or neither) is a compile-time error, so
 * the component can never be rendered in an undefined-state that would
 * crash at runtime.
 */
type Props = BaseProps &
	(
		| {
				/** Single-destination card — whole card becomes a link. */
				openLink: HomeSectionOpenLink;
				subLinks?: never;
				subLinksTwoColumn?: never;
		  }
		| {
				/**
				 * Multi-destination card — passive card with a list of blue
				 * hyperlinks. Sub-links with a `permission` the user lacks are
				 * filtered out; if the filter removes them all the card is
				 * hidden entirely.
				 */
				subLinks: HomeSectionSubLink[];
				/**
				 * Render {@link subLinks} as a two-column grid inside the card
				 * body. Useful for sections with many children (e.g. App
				 * homepage with 8 sub-pages).
				 */
				subLinksTwoColumn?: boolean;
				openLink?: never;
		  }
	);

const HomeSectionCard: FC<Props> = ({
	title,
	description,
	icon: Icon,
	openLink,
	subLinks,
	subLinksTwoColumn
}) => {
	const { hasPermission, isLoading } = usePermissions();

	// Match UserAdminFilter: while the permissions query is loading, stay
	// hidden rather than show the card only to collapse it a beat later
	// once permissions arrive. If the query errors out before any data has
	// been fetched, `hasPermission` returns false for every check (because
	// `query.data` is undefined) and the natural filter fall-through hides
	// sub-links the user can't demonstrate permission for. If the query
	// errors out *after* a prior successful fetch, React Query keeps the
	// last-known `data` on the query, so `hasPermission` keeps returning
	// the last-known answer — users continue to see the links they had
	// before the refresh failed, which is the behaviour we want.
	if (isLoading) return null;

	const cardBase =
		"flex h-full flex-col overflow-hidden rounded-xl border border-border-light bg-white shadow-sm transition-all duration-200";
	const cardHover = "group-hover:shadow-md group-hover:-translate-y-px";

	const header = (
		<div className="flex items-center justify-between px-6 py-5 border-b border-border">
			<h3 className="font-heading text-lg font-semibold text-foreground">
				{title}
			</h3>
			<div
				aria-hidden
				className="flex h-10 w-10 shrink-0 items-center justify-center text-muted-foreground"
			>
				<Icon className="h-6 w-6" />
			</div>
		</div>
	);

	if (openLink) {
		// Single-destination: whole card is a link.
		return (
			<Link to={openLink.path} className="group block h-full">
				<div className={`${cardBase} ${cardHover}`}>
					{header}
					<div className="flex flex-1 flex-col px-6 py-5">
						<p className="text-sm leading-relaxed text-muted-foreground mb-4">
							{description}
						</p>
						<span className="mt-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors group-hover:text-primary-dark group-hover:underline underline-offset-[3px]">
							{openLink.label}
							<ArrowRight
								aria-hidden
								className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
							/>
						</span>
					</div>
				</div>
			</Link>
		);
	}

	// Multi-destination path. The discriminated union guarantees subLinks
	// is defined here, so no non-null assertion is needed.
	const visibleSubLinks = subLinks.filter(
		(link) => !link.permission || hasPermission(link.permission)
	);

	// Hide the card entirely when every sub-link has been filtered out —
	// there's nothing useful to show.
	if (visibleSubLinks.length === 0) return null;

	const listClass = subLinksTwoColumn
		? "grid grid-cols-2 gap-x-4 gap-y-1"
		: "flex flex-col gap-1";

	return (
		<div className={cardBase}>
			{header}
			<div className="flex flex-1 flex-col px-6 py-5">
				<p className="text-sm leading-relaxed text-muted-foreground mb-4">
					{description}
				</p>
				<ul className={listClass}>
					{visibleSubLinks.map(({ label, path, icon: LinkIcon }) => (
						<li key={path}>
							<Link
								to={path}
								className="inline-flex items-center gap-2 py-1 text-sm font-medium text-primary transition-colors hover:text-primary-dark hover:underline underline-offset-[3px]"
							>
								<LinkIcon
									aria-hidden
									className="h-4 w-4 text-muted-foreground"
								/>
								{label}
							</Link>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default HomeSectionCard;
